import React, { useState, useEffect, useCallback } from 'react';
//components
import Checkbox from "../../../../components/ui/checkbox/Checkbox"
import Label from '../../../../components/ui/labels/Label';
import { Button } from '../../../../components/ui/buttons/Button';
import { PoiTableRow } from './components/PoiTableRow'
import { PoiTableHeader } from './components/PoiTableHeader';
import { AGMTableRow } from './components/AGMRow';
import { SelectContainer } from '../../../../components/ui/layout/SelectContainer';
import Select from 'react-select'
//hooks/context
import usePoiGroupService from '../../../../hooks/pois/usePoiGroupsService';
import { useLoading } from '../../../../contexts/LoadingContext';
import { useError } from '../../../../contexts/ErrorContext';
import { useSuccess } from '../../../../contexts/SuccessContext'
import { useUnsavedChanges } from '../../../../contexts/UnsavedChangesContext';
import useConfirmDialog from '../../../../hooks/useConfirmDialog'
import { useUser } from '../../../../contexts/UserContext';
import useLiveSiteMap from '../../../../hooks/useLiveSiteMap';
//api
import { getPOIsAPI, createPOIAPI, updatePOIAPI, deletePOIByIDAPI } from "../../../../services/poiAPI"
import { getPOIsByGroupAPI, getPOIGroupsByJobAPI } from "../../../../services/poiGroupsAPI"
import { getAGMsAPI } from "../../../../services/agmsAPI";
//utils
import { sortPoisAndAgms } from "../../../../utils/poi/poiTableUtils/sortPoisAndAgms"
import { validatePoiTable } from "../../../../utils/poi/poiTableUtils/validatePoiTable"
import { arePoiGroupsEqual, areLinkedPOIsEqual } from '../../../../utils/poi/poiTableUtils/arePoiGroupsEqual'
import { nearestNeighbor } from '../../../../utils/locationUtils';

/**
 * 
 * @param {DropdownOption} selectedJob 
 * @param {Function} setOpenPoiOnMap - for choosing a single POI (based on POIID) to view on the map
 * @param {Function} setActiveComponent - for changing the tab to Map component when selecting a POI to view on map
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - POI
 */

//new rows have no POIID until the server gives them one, but we still need to identify them
//in state - to know which of a batch of new POIs failed to save. stripped before we send to the api
let tempPoiIdCounter = 0
const getTempPoiId = () => `temp-poi-${++tempPoiIdCounter}`

//stable react key for a table row - the table holds both POIs and AGMs.
//saved POIs key on POIID, unsaved ones on _tempId, so a row keeps its identity when the table re-sorts.
//deliberately no index fallback: every row should have one of these three, and a duplicate key
//warning is a better signal than silently falling back to positional keys
const getRowKey = (item) => item.AGMID ? `agm-${item.AGMID}` : `poi-${item.POIID ?? item._tempId}`

export const PoiTable = ({
  selectedJob, 
  setOpenPoiOnMap, 
  setActiveComponent,
  storedPoiGroup,
  setStoredPoiGroup,
}) => {
  //state
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [showAgms, setShowAgms] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [fetchedByGroupId, setFetchedByGroupId] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [submitErrors, setSubmitErrors] = useState([])
  const [groupOptionsForJob, setGroupOptionsForJob] = useState([]) //to fill group selection dropdown, need all groups, not just user's

  //check if poi data has agms 
  const dataHasAgms = data.some(item => item.AGMID !== undefined);

  //the table holds agms as well as pois, so 'is the table empty' has to mean 'are there no POI rows'.
  //keying the empty state off data.length hid the add poi button whenever agms were present but pois weren't
  const poiRows = data.filter(item => !item.AGMID);

  //hooks
  const { fetchPOIGroupsByUser, updatePoiGroups, poiGroupOptions, usersGroupData, formatPoiGroupOptions} = usePoiGroupService()
  const { fetchRouteArr, polyLineArr} = useLiveSiteMap()
  const { hideLoading, showLoading, isLoading } = useLoading()
  const { showError } = useError()
  const { timeoutSuccess } = useSuccess()
  const { setHasUnsavedChanges } = useUnsavedChanges()
  const [ConfirmDialog, requestConfirm] = useConfirmDialog()
  const { userGroups, user } = useUser()

  const userIsAdmin = userGroups.includes('Admin')

  useEffect(() => {
    if(storedPoiGroup && !storedPoiGroup.label.includes("Don't show")){
      setSelectedGroup(storedPoiGroup)
    }
  }, [storedPoiGroup])

  useEffect(() => {
    if (!selectedJob) return

    //fetch all groups for job
    fetchPOIGroupForJob(selectedJob.value)

    //fetch groups for user
    fetchPOIGroupsByUser(selectedJob.value, true) //add true to remove extra options in multi select
    fetchRouteArr(selectedJob.value)

    //reset state
    setValidationErrors([])
    setSubmitErrors([])
    setData([])
    setOriginalData([])
    setSelectedGroup(!storedPoiGroup?.label.includes("Don't show") ? storedPoiGroup : null)
    setFetchedByGroupId(null)
  }, [selectedJob]); //eslint-disable-line

  useEffect(() => {
    if(!selectedGroup) return;

    fetchData(selectedJob.value, selectedGroup) 

    setValidationErrors([])
    setSubmitErrors([])
  }, [selectedGroup]); //eslint-disable-line

  //fetch poi groups for job
  const fetchPOIGroupForJob = async(jobId) => {

    const response = await getPOIGroupsByJobAPI(jobId)

    if (!response.success) {
        showError(`Error getting POI groups for job: ${response.message}`);
    } else {

        const groupOptions = formatPoiGroupOptions(response.data, true)
            
        setGroupOptionsForJob(groupOptions)

    }
  }

  // fetch pois and agms, sort them
  const fetchData = async (jobID, selectedGroup) => {
    showLoading("Getting POIs and AGMs...")
    
    //fetch
    let poiResponse

    if(selectedGroup.label.includes("All POIs")){
      poiResponse = await getPOIsAPI(jobID)
      setFetchedByGroupId(null)
      
    } else {
        poiResponse = await getPOIsByGroupAPI(selectedGroup.value)
        setFetchedByGroupId(selectedGroup.value)
    }

    if (!poiResponse.success) {
      hideLoading()
      showError(`Error getting POIs: ${poiResponse.message}`)

      //without this we carry on and throw reading .pois off an undefined data
      return
    }

    const pois = poiResponse.data.pois ? poiResponse.data.pois : poiResponse.data;

    const poisWithUsersGroups = pois.map(poi => {
        const usersGroupsForPOI = usersGroupData.filter(userGroup =>
          poi.POIGroups.some(poiGroup => poiGroup.POIGroupID === userGroup.POIGroupID)
        );

        const formattedGroups = formatPoiGroupOptions(usersGroupsForPOI, true)

      return { ...poi, POIGroups: formattedGroups };
    });


    const agmResponse = await getAGMsAPI(jobID)

    //sort in order of where they appear on pipeline
    //use upstream and downstream site from poi to put in order
    if (!agmResponse.success) {
      hideLoading()
      showError(`Error getting AGMs: ${agmResponse.message}`)
    } else {

      //both branches used to require pois, so a job with agms and no pois loaded nothing at all -
      //which hid the show AGMs toggle and left the agm rows (and their 'add poi below' buttons)
      //unreachable until the job had at least one poi. sortPoisAndAgms handles either side being empty
      const nnAGMs = agmResponse.data.length > 0
        ? nearestNeighbor(agmResponse.data).map(agmData => {
            return {
              ...agmData,
              agm: true
            }
          })
        : []

      //basic nn array is good enough for displaying on table, don't need adapted coordiates array here
      const dataArr = sortPoisAndAgms(poisWithUsersGroups, nnAGMs)

      setData(dataArr)

      const dataDeepCopy = JSON.parse(JSON.stringify(dataArr))
      setOriginalData(dataDeepCopy)

      hideLoading()
    }

  }


  // Function to handle adding a new POI

  const addPoi = useCallback((index) => {

    const newPoi = {
      POIID: null,
      _tempId: getTempPoiId(),
      POI_Name: '',
      Description: '',
      POIGroups: selectedGroup?.label.includes("All POIs") ? [] : [selectedGroup],
      Intersect_Point_Lat: '',
      Intersect_Point_Lng: '',
      Click_Point_Lat: '',
      Click_Point_Lng: '',
      Dist_To_US: '',
      Dist_To_DS: '',
      US_Site: '', //US/DS calculated when lat/lng entered in row
      DS_Site: '',
      CreatedBy: {
        UserID: user.UserID,
        First_Name: user.First_Name,
        Last_Name: user.Last_Name
      }
    };

    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 0, newPoi);
      return newData; 
  });

  }, [setData, user, selectedGroup])

  const handlePoiChange = useCallback((updates, index) => {

    setData(prevData => {
      // Copy previous data
      const newData = [...prevData];
  
      // Copy data from the current row, overwrite properties with changes from the updates object
      newData[index] = {
        ...newData[index],
        ...updates // Spread updates to apply multiple changes
      };

      return newData;
    });
  
    setHasUnsavedChanges(true);
  }, [setData, setHasUnsavedChanges]);
  

  // Function to handle group selection
  const handleGroupChange = useCallback((selectedGroups, index) => {

    setData(prevData => {

      //copy previous data
      const newData = [...prevData];

      //copy data from current row, overwrite property with changed value
      newData[index].POIGroups = selectedGroups;

      return newData;
    });
  }, [setData])

  //helper functions

  const handleSubmit = async () => {

    //show loading message, remove old submit message if exists
    showLoading("Submitting...")

    const isValid = validatePoiTable(data, setValidationErrors)

    //do we need to show an error message?
    if(!isValid){
      hideLoading();
      showError("Some required fields are not filled out - see fields outlined in red.")
      return;
  } 

    //submit POIs to api  
    //api expects arrays of POIs, so create empty arrays to push new/updated POIs into
    let newPOIs = []
    let updatedPOIs = []
    let agms = []

    for (const item of data) {

      //don't want to include agms - just for submitting POIs
      if (item.hasOwnProperty("AGMID")) {
        agms.push(item)
        continue
      }

      //UPDATE POIs - POIs that are not new (have a POI) and are different from the original data (to reduce api calls)
      //destructure out CreatedBy and linkedPOIs as they won't change in this current table setup. Also extract poi groups - they are compared separately
      if (item.POIID) {

        const { 
          CreatedBy, 
          AltCreatedBy,
          POIGroups, 
          linkedPOIs, 
          ...poiData 
        } = item;
        //compare to original data to see if POI data has changed
        const originalRow = originalData.find(original => original.POIID === item.POIID)
        const { 
          CreatedBy: originalCreatedBy, 
          AltCreatedBy: altOriginalCreatedBy, 
          POIGroups: originalPOIGroups, 
          linkedPOIs: originalLinkedPOIs, 
          ...originalPoiData } = originalRow
        
          const changedFields = Object.keys(poiData).reduce((acc, key) => {
            if (JSON.stringify(poiData[key]) !== JSON.stringify(originalPoiData[key])) {
              acc[key] = poiData[key];
            }
            return acc;
          }, {});

        if (Object.keys(changedFields).length > 0 || 
            !arePoiGroupsEqual(POIGroups, originalPOIGroups) ||
            !areLinkedPOIsEqual(linkedPOIs, originalLinkedPOIs)
          ) {
          updatedPOIs.push({
            POI_Name: item.POI_Name,
            Description: item.Description,
            ...changedFields,
            POIID: item.POIID,
            POIGroups,
            linkedPOIs,
            CreatedBy: CreatedBy.UserID
          })
        }


      } else {

        //CREATE pois - if no POIID, it's a new POI
        //keep _tempId alongside (not inside) the payload so we can match the response back to this row
        const { _tempId, ...itemForApi } = item

        const newPOI = {
          ...itemForApi,
          JobID: selectedJob.value
        }

        //sourceItem lets us find this exact row again if the create fails, without relying on _tempId
        newPOIs.push({ tempId: _tempId, sourceItem: item, payload: newPOI })
      }

    }

    //if no changes made, no need to update state, also don't want to tell user it saved when nothing happened
    if (newPOIs.length === 0 && updatedPOIs.length === 0) {
      setHasUnsavedChanges(false)

      hideLoading()

      timeoutSuccess("No changes")

      return
    }

    //submit updated POIs are if there are any
    if (updatedPOIs.length > 0) {

      //if not fetched by group, we can update the original pois.
      //admins can also change the original poi from group view - matches PoiDialog, which saves the poi as
      //well as the linkedPOI for admins. without this the table accepted lat/lng edits in group view and
      //silently dropped them, then reported 'Saved'
      if(!fetchedByGroupId || userIsAdmin){

        const poiResponses = await Promise.all(
          //AltPOI_Name/AltDescription are the linkedPOI's POI_Name/Description under another name - they
          //aren't columns on POI, so keep them out of this payload. updatePoiGroups saves them below
          updatedPOIs.map(async ({ POIGroups, linkedPOIs, AltPOI_Name, AltDescription, ...poi }) => {
            const response = await updatePOIAPI(poi);
            return { ...response, POIID: poi.POIID }; // Attach POIID to track which POI failed
          })
        );

        const failedPOIUpdates = poiResponses.filter(response => !response.success);

        if (failedPOIUpdates.length > 0) {
          hideLoading();
          const errors = []

          failedPOIUpdates.forEach(({POIID}) => {
              errors.push(POIID)
          });

          setSubmitErrors(errors);

          showError(`Error updating POIs: ${failedPOIUpdates.map(f => f.message).join(", ")}`);
          return;
        }

      }

      //update poi groups
      const poiGroupResponses = await Promise.all(
        updatedPOIs.map(async ({POIGroups, ...poi}) => {
        const originalRow = originalData.find(original => original.POIID === poi.POIID)
        const originalPOIGroups = originalRow?.POIGroups ?? []

          const response = await updatePoiGroups(POIGroups, originalPOIGroups, poi.linkedPOIs, poi.POIID, selectedJob.value);
          return { ...response, POIID: poi.POIID };
        })
      );

      const failedPOIGroupUpdates = poiGroupResponses.filter(response => !response.success);

      if (failedPOIGroupUpdates.length > 0) {
        hideLoading();
        const errors = []

        failedPOIGroupUpdates.forEach(({POIID}) => {
            errors.push(POIID)
        });

        setSubmitErrors(errors);

        showError(`Error updating POI groups: ${failedPOIGroupUpdates.map(f => f.message).join(", ")}`);
        return;
      }

    }

    //get all the POIs that aren't new, for updating state later
    let poisWithIds = data.filter(item => item.POIID)


    //submit new POIs are if there are any
    if (newPOIs.length > 0) {

      const responses = await Promise.all(
        newPOIs.map(async ({ tempId, sourceItem, payload }) => {
          const formattedGroups = payload.POIGroups.map(group => {
              return {
                POIGroup_Name: group.label,
                POIGroupID: group.value
              }
            })
          const response = await createPOIAPI({
            ...payload,
            POIGroups: formattedGroups
          });
          return { ...response, tempId, sourceItem } //attached to track which new POI failed
        })
      );

      //these creates all ran, so any that succeeded are already in the db. we have to take their
      //POIIDs into state even when a sibling failed - otherwise those rows still look new and a
      //second Save would create them all over again as duplicates
      const successfulCreates = responses.filter(response => response.success)
      const failedCreates = responses.filter(response => !response.success)

      //format new POIs, now with POIIDs
      //these rows now stay in state instead of the table being cleared, so they need the same shape as
      //fetched rows. PoiTableRow and the change comparison both expect a linkedPOIs array
      const newPois = successfulCreates.map((poi) => {
        const linkedPOIs = poi.data.linkedPOIs ?? []

        //in group view, fetched rows also carry their linkedPOI flattened on top (LPOIID/AltPOI_Name/
        //AltDescription - see getPOIsByGroup in the api), and PoiTableRow routes name/description edits
        //through poi.LPOIID. without the flatten here, editing a just-created row updated nothing in
        //linkedPOIs, so the edit was silently dropped on submit until the page was refreshed
        const linkedPOIForGroup = fetchedByGroupId
          ? linkedPOIs.find(lpoi => lpoi.POIGroupID === fetchedByGroupId)
          : null

        return {
          ...poi.data,
          POIGroups: poi.data.POIGroups ? formatPoiGroupOptions(poi.data.POIGroups, true) : [],
          linkedPOIs,
          ...(linkedPOIForGroup && {
            LPOIID: linkedPOIForGroup.LPOIID,
            AltPOI_Name: linkedPOIForGroup.POI_Name,
            AltDescription: linkedPOIForGroup.Description
          })
        }
      })
      poisWithIds.push(...newPois)

      if (failedCreates.length > 0) {
        //keep the rows that failed so the user can correct and retry them. they still have no
        //POIID, so they'll be picked up as new POIs on the next Save - which is now correct,
        //because the ones that did save are no longer sitting in state without an id
        const failedTempIds = failedCreates.map(({ tempId }) => tempId).filter(Boolean)

        const failedRows = failedCreates.map(({ sourceItem }) => sourceItem)

        const sortedPartialData = sortPoisAndAgms([...poisWithIds, ...failedRows], agms)

        setData(sortedPartialData)

        //only the successfully saved rows form the new baseline for future update diffs
        const savedOnlyDeepCopy = JSON.parse(JSON.stringify(sortedPartialData.filter(item => item.POIID)))
        setOriginalData(savedOnlyDeepCopy)

        setSubmitErrors(failedTempIds)

        //there are still unsaved rows on the table, so this must stay true
        setHasUnsavedChanges(true)

        hideLoading()

        showError(`Error creating POI: ${failedCreates.map(f => f.message).join(", ")}`)

        return
      }
    }

    const sortedData = sortPoisAndAgms(poisWithIds, agms)

    //update state
    setData(sortedData)

    const sortedDataDeepCopy = JSON.parse(JSON.stringify(sortedData))
    setOriginalData(sortedDataDeepCopy)

    //everything saved - clear any rows still flagged from a previous failed submit.
    //this used to live inside the 'if there are updated POIs' block, so a creates-only save left them red
    setSubmitErrors([])

    setHasUnsavedChanges(false)

    hideLoading()

    timeoutSuccess("Saved")

  }

  //delete agms 

  const handleDeleteOnePOI = useCallback(async (rowData, rowIndex) => {
    showLoading("Deleting POI...")

    const POIID = rowData.POIID

    //if there is no ID, doesn't exist yet in database, so don't need to call API
    //just delete from table in that case
    if (POIID) {
      const isConfirmed = await requestConfirm('Are you sure you want to delete this POI?', 'Delete POI', 'Cancel');

      if (!isConfirmed) {
        hideLoading()

        return
      }

      const response = await deletePOIByIDAPI({ POIID, CreatedBy: rowData.CreatedBy })

      if (!response.success) {
        hideLoading()

        showError(`Error deleting POI: ${response.message}`)

        return

      }

      timeoutSuccess("Delete successful")

    }

    //delete successful - remove item from data and let user know
    setData(prevData => {

      let newData = []

      newData = [...prevData];

      newData.splice(rowIndex, 1);

      return newData;
    });

    hideLoading()

  }, [showLoading, deletePOIByIDAPI, hideLoading, setData]); //eslint-disable-line

  //delete all pois - admin only
  const handleDeleteAllPOIs = async () => {
    //check that user wants to delete agms
    const isConfirmed = await requestConfirm('Are you sure you want to delete all POIs?', 'Delete POIs', 'Cancel');
    if (!isConfirmed) return

    // user confirmed
    showLoading("Deleting POIs...")

    //if agm has id, it exists in database, so need to delete it from there
    const deletePOIsFromDB = data
      .filter(poi => poi.POIID !== undefined)
      .map(poi => {
        return {
          POIID: poi.POIID, CreatedBy: poi.CreatedBy
        }
      }
      );

    if (deletePOIsFromDB.length > 0) {

      //TODO: maybe api should take an array of pois instead of just one? then we don't have to loop here and if something goes wrong transaction could be rolled back
      for (const poi of deletePOIsFromDB) {
        const response = await deletePOIByIDAPI(poi)

        if (!response.success) {
          hideLoading()

          showError(`Error deleting POIs: ${response.message}`)

          return
        }
      }

    }

    //everything submitted successfully, so let user know and reset table
    setData([])
    setOriginalData([])

    setSubmitErrors([])

    hideLoading()

    setHasUnsavedChanges(false)

    timeoutSuccess("Delete successful")

  }

  const handleOpenPoiOnMap = useCallback((poiId, poiGroupId = null) => {
    setOpenPoiOnMap({ poiId, jobId: selectedJob.value, poiGroupId })
    setActiveComponent('Map')
  }, [setOpenPoiOnMap, setActiveComponent, selectedJob])

  const handleChangeGroup = (group) => {
    setStoredPoiGroup(group)
    setSelectedGroup(group)
  }

  if (!selectedJob) {
    return <div className='martrak-page-padding-left-right'>Please select a job</div>
  }

  return (
    <div className='martrak-page-padding-left-right'>

      {/* group selector */}
      <Label>Select POIs by Group</Label>
      <SelectContainer>
        <Select
          value={selectedGroup}
          options={[{label: "All POIs for job", value: 0},...groupOptionsForJob]}
          name="poiGroupDropdown"
          onChange={(x) => handleChangeGroup(x)}
          menuPosition='fixed'
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        />
      </SelectContainer>

      {/* toggle to show or hide AGMs */}
      {dataHasAgms && <div className='flex gap-2'>
        <Label forId="agm-toggle">Show AGMs</Label>
        <Checkbox
          name='agm-toggle'
          onChange={() => setShowAgms(!showAgms)}
          initiallyChecked={showAgms}
          checked={showAgms}
        />
      </div>}

      {/* poi table */}
      <div className="overflow-x-auto overflow-y-auto max-h-96 max-w-full">

        <table className="min-w-full border-collapse table-auto bg-white">

          <PoiTableHeader 
            fetchedByGroupId={fetchedByGroupId} 
            userIsAdmin={userIsAdmin}  
          />

          <tbody>

            {/* show 'select pois' message if no group selected */}
            {!selectedGroup ? (
              <tr>
                  <td colSpan={13} className="text-center p-4 text-gray-500">
                    Please select POIs
                  </td>
                </tr>
            ) :

            //rows first, then the empty state underneath them. these used to be two arms of the same
            //ternary, so agm rows and the 'no POIs yet' message could never show at the same time - which
            //is why a job with agms and no pois had no way to add a poi from the table
            (
              <>
              {data.map((item, itemIndex) => (
                <React.Fragment key={getRowKey(item)}>

                  {/* if item is a POI, add a POI specific row */}
                  {!item.AGMID ? (

                    <PoiTableRow
                      poi={item}
                      poiIndex={itemIndex}
                      handlers={{
                        handlePoiChange,
                        handleGroupChange,
                        addPoi,
                        handleDeleteOnePOI,
                        handleOpenPoiOnMap
                      }}
                      poiGroupOptions={poiGroupOptions}
                      polyLineArr={polyLineArr}
                      errors={{
                        validationErrors: validationErrors.filter(error => error.row === itemIndex),
                        //new rows have no POIID yet, so they're tracked by _tempId instead
                        submitError: submitErrors.includes(item.POIID ?? item._tempId)
                      }}
                      userPermissions={{
                        userIsAdmin,
                      }}
                      fetchedByGroupId={fetchedByGroupId}
                    />

                  )
                    // if the item is an AGM, add a row that has only AGM specific data filled in
                    : item.AGMID && showAgms ? (

                        <AGMTableRow
                          item={item}
                          fetchedByGroupId={fetchedByGroupId}
                          itemIndex={itemIndex}
                          addPoi={addPoi}
                          userIsAdmin={userIsAdmin}
                        />

                    ) : null}

                </React.Fragment>
              ))}

              {/* no pois yet - tell the user, and for admins offer to add one.
                  this now renders alongside any agm rows rather than instead of them */}
              {poiRows.length === 0 && !isLoading ? (
                <>
                  <tr>
                    <td colSpan={13} className="text-center p-4 text-gray-500 w-96">
                      <p>There are no POIs {selectedGroup.label.includes("All POIs") ? 'for this job' : 'in this group'}. {userIsAdmin ? 'Add a new POI below or on the map.' : 'Add a new POI on the map.'}</p>
                    </td>
                  </tr>

                  {/* extra row to add POI - admin only */}
                  {userIsAdmin ?
                    <tr>
                      <td colSpan={13} className="text-center p-4 text-gray-500 w-96">
                        <button
                          onClick={() => addPoi(data.length)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Add POI
                        </button>
                      </td>
                    </tr>
                  : null}
                </>
              ) : null}
              </>
            )}
          </tbody>

        </table>

      </div>

      {/* submit and delete buttons */}

      {/* gated on poiRows, not data - data now includes agms, and neither button makes sense with no POIs */}
      <div className='flex mt-8'>
        {poiRows.length > 0 &&
          <Button
            variant="success"
            onClick={() => handleSubmit()}
            className='w-max mr-2'
            disabled={!userIsAdmin}
          >
            Submit
          </Button>
        }

        {/* only allow user to delete all pois for job if they are admin - can changes this if we want looser permissions for this */}
        {/* hidden rather than disabled for non-admins - no reason to show a destructive button they can't use */}
        {
          poiRows.length > 0 && userIsAdmin ?
            <Button
              variant="danger"
              onClick={() => handleDeleteAllPOIs()}
              className='w-max mr-2'
            >
              <i className="fa-solid fa-trash-can mr-2"></i>
              Delete All POIs
            </Button>
            : null
        }
      </div>

      <ConfirmDialog />

    </div>
  );
};